import { Content, Epic } from '@amzn/awscat-pattern-library-service-model';

import { reorderById } from '../common/Arrays';
import JSONSerializer from '../common/JSONSerializer';
import { MissingSectionError, SpecialSectionId, updateSpecialSection } from './SectionOperations';

/** Shared serializer — validates shape on hydration, throws on corruption. */
const SERIALIZER = new JSONSerializer();

/**
 * Locates the 'epics' Pattern section and returns the raw contentData.
 * Returns `undefined` when the section is missing or empty.
 */
export function rawEpicsData(content: Content): unknown[] | undefined {
  const section = content.contentData.contentSections?.find((s) => s.id === SpecialSectionId.Epics);
  const data = section?.contentData;
  if (data == null) return undefined;
  return Array.isArray(data) ? data : undefined;
}

/**
 * Writes a new epics array into the content. Returns `undefined` when
 * the 'epics' section is missing (instead of crashing).
 */
function tryWriteEpics(content: Content, next: readonly Epic[]): Content | undefined {
  try {
    return updateSpecialSection(content, SpecialSectionId.Epics, next);
  } catch (e) {
    if (e instanceof MissingSectionError) return undefined;
    throw e;
  }
}

/**
 * Returns the typed Epic[] for the Pattern's 'epics' section.
 * Returns `[]` when the section is missing or empty.
 *
 * @throws When the data is present but doesn't match the Epic schema
 * (indicates corrupted data — not expected to be caught).
 */
export function listEpics(content: Content): readonly Epic[] {
  const data = rawEpicsData(content);
  if (!data) return [];

  // Hydrate via JSONSerializer for shape validation.
  return SERIALIZER.deserializeArray<Epic>(data, Epic);
}

/**
 * Appends `epic` to the Pattern's epics list. Returns `undefined`
 * when the 'epics' section is missing or when an epic with the same
 * id already exists.
 */
export function tryAddEpic(content: Content, epic: Epic): Content | undefined {
  const epics = listEpics(content);

  // Duplicate guard — may happen via a race.
  if (epics.some((e) => e.id === epic.id)) return undefined;

  return tryWriteEpics(content, [...epics, epic]);
}

/**
 * Replaces the epic identified by `epicId` with `setter(current)`.
 * Returns `undefined` when the epic does not exist or the section is
 * missing.
 */
export function tryUpdateEpic(
  content: Content,
  epicId: string,
  setter: (epic: Epic) => Epic,
): Content | undefined {
  const epics = listEpics(content);
  const index = epics.findIndex((e) => e.id === epicId);
  if (index < 0) return undefined;

  const next = epics.slice();
  // @ts-expect-error TS2345 - strict migration
  next[index] = setter(epics[index]);
  return tryWriteEpics(content, next);
}

/**
 * Removes the epic identified by `epicId`. Returns `undefined` when
 * the epic does not exist or the section is missing.
 */
export function tryDeleteEpic(content: Content, epicId: string): Content | undefined {
  const epics = listEpics(content);
  if (!epics.some((e) => e.id === epicId)) return undefined;

  return tryWriteEpics(
    content,
    epics.filter((e) => e.id !== epicId),
  );
}

/**
 * Reorders epics to match `newOrderIds`. Returns `undefined` when the
 * section is missing.
 */
export function tryReorderEpics(
  content: Content,
  newOrderIds: readonly string[],
): Content | undefined {
  const next = reorderById(listEpics(content), newOrderIds);
  return tryWriteEpics(content, next);
}
